import React, { useState, useEffect, useCallback } from "react";
import { Calendar, momentLocalizer, Event, View } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { ScheduleWithCompany, CreateScheduleRequest } from "../types/schedule";
import { Company } from "../types/company";
import { ApiResponse, CompanyApiResponse } from "../types/auth";
import { ScheduleApiResponse } from "../types/schedule";
import { FaFilter } from "react-icons/fa";

// momentのロケールを日本語に設定
moment.locale("ja");
const localizer = momentLocalizer(moment);

// カレンダーのイベント型定義
interface CalendarEvent extends Event {
  id: number;
  title: string;
  start: Date;
  end: Date;
  resource: ScheduleWithCompany;
}

const SchedulesPage: React.FC = () => {
  const [schedules, setSchedules] = useState<ScheduleWithCompany[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null
  );
  const [view, setView] = useState<View>("month");
  const [date, setDate] = useState<Date>(new Date());
  const [filterCompanyIds, setFilterCompanyIds] = useState<number[]>([]);
  const [filterScheduleTypes, setFilterScheduleTypes] = useState<string[]>([]);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [editingSchedule, setEditingSchedule] =
    useState<ScheduleWithCompany | null>(null);
  const [showAllToday, setShowAllToday] = useState<boolean>(false);
  const [showAllWeek, setShowAllWeek] = useState<boolean>(false);
  const [showAllFuture, setShowAllFuture] = useState<boolean>(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5173";

  // スケジュール一覧を取得
  const fetchSchedules = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/schedules`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data: ApiResponse<ScheduleApiResponse> | any =
          await response.json();

        // 柔軟に受け付ける: data.data が配列の場合や { schedules: [...] } の場合に対応
        const payload = data.data;
        let schedulesPayload: ScheduleWithCompany[] = [];

        if (Array.isArray(payload)) {
          schedulesPayload = payload;
        } else if (payload && Array.isArray(payload.schedules)) {
          schedulesPayload = payload.schedules;
        } else if (payload && payload.schedule) {
          schedulesPayload = [payload.schedule];
        }

        if (data.success && schedulesPayload.length > 0) {
          setSchedules(schedulesPayload);
        } else {
          setSchedules([]);
        }
      } else {
        setError("スケジュール一覧の取得に失敗しました");
      }
    } catch (error) {
      setError("スケジュール一覧の取得中にエラーが発生しました");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  // 企業一覧を取得
  const fetchCompanies = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/companies`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data: ApiResponse<CompanyApiResponse> = await response.json();

        // data.dataが直接配列の場合と、data.data.companiesの場合の両方に対応
        if (data.success && Array.isArray(data.data)) {
          setCompanies(data.data);
        } else if (data.success && data.data?.companies) {
          setCompanies(data.data.companies);
        } else {
          setCompanies([]);
        }
      }
    } catch (error) {}
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchSchedules();
    fetchCompanies();
  }, [fetchSchedules, fetchCompanies]);

  // スケジュールをカレンダーイベントに変換（フィルタリング適用）
  const events: CalendarEvent[] = schedules
    .filter((schedule) => {
      // 企業フィルター（選択された企業IDのいずれかに一致するか）
      if (
        filterCompanyIds.length > 0 &&
        !filterCompanyIds.includes(schedule.company_id)
      ) {
        return false;
      }
      // スケジュール種類フィルター（選択された種類のいずれかに一致するか）
      if (
        filterScheduleTypes.length > 0 &&
        !filterScheduleTypes.includes(schedule.schedule_type)
      ) {
        return false;
      }
      return true;
    })
    .map((schedule) => ({
      id: schedule.id,
      title: `${schedule.company_name} - ${getScheduleTypeLabel(
        schedule.schedule_type
      )}`,
      start: new Date(schedule.schedule_start_date),
      end: new Date(schedule.schedule_end_date),
      resource: schedule,
    }));

  // スケジュール追加
  const handleAddSchedule = async (scheduleData: CreateScheduleRequest) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/schedules`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(scheduleData),
      });

      if (response.ok) {
        setShowForm(false);
        fetchSchedules();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "スケジュールの登録に失敗しました");
      }
    } catch (error) {
      setError("スケジュールの登録中にエラーが発生しました");
    }
  };

  // スケジュール編集
  const handleEditSchedule = async (scheduleData: CreateScheduleRequest) => {
    if (!editingSchedule) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/schedules/${editingSchedule.id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(scheduleData),
        }
      );

      if (response.ok) {
        setEditingSchedule(null);
        setSelectedEvent(null);
        fetchSchedules();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "スケジュールの更新に失敗しました");
      }
    } catch (error) {
      setError("スケジュールの更新中にエラーが発生しました");
    }
  };

  // スケジュール削除
  const handleDeleteSchedule = async (scheduleId: number) => {
    if (!window.confirm("このスケジュールを削除しますか？")) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/schedules/${scheduleId}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        setSelectedEvent(null);
        fetchSchedules();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "スケジュールの削除に失敗しました");
      }
    } catch (error) {
      setError("スケジュールの削除中にエラーが発生しました");
    }
  };

  // イベントクリック時の処理
  const handleSelectEvent = (event: CalendarEvent) => {
    setSelectedEvent(event);
  };

  // イベントスタイルのカスタマイズ
  const eventStyleGetter = (event: CalendarEvent) => {
    const schedule = event.resource;
    const colorMap: { [key: string]: string } = {
      es_deadline: "#e74c3c",
      internship: "#3498db",
      interview: "#f39c12",
      screening: "#9b59b6",
      orientation: "#1abc9c",
      other: "#95a5a6",
    };

    const backgroundColor = colorMap[schedule.schedule_type] || "#95a5a6";

    return {
      style: {
        backgroundColor,
        borderRadius: "5px",
        opacity: 0.9,
        color: "white",
        border: "0px",
        display: "block",
      },
    };
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-16 text-gray-600">
        <div className="loading-spinner"></div>
        <span>読み込み中...</span>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto p-8">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="bg-transparent border-none text-red-700 text-xl cursor-pointer px-2"
          >
            ×
          </button>
        </div>
      )}

      {/* ヘッダー: ボタンとフィルター */}
      <div className="bg-white rounded-lg p-3 mb-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          {/* スケジュール追加ボタン */}
          <button
            className="bg-gradient-to-br from-blue-500 to-green-600 text-white border-none py-2 px-4 rounded-lg text-base font-medium cursor-pointer transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5"
            onClick={() => setShowForm(true)}
          >
            + スケジュールを追加
          </button>

          {/* フィルターボタン */}
          <button
            onClick={() => setShowFilter(!showFilter)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-all ${
              showFilter
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <span>
              <FaFilter />
            </span>
            <span>フィルター</span>
            {(filterCompanyIds.length > 0 ||
              filterScheduleTypes.length > 0) && (
              <span className="bg-white text-blue-600 px-2 py-0.5 rounded-full text-xs font-bold">
                {filterCompanyIds.length + filterScheduleTypes.length}
              </span>
            )}
          </button>

          {/* 選択中のフィルタータグ */}
          {filterCompanyIds.length > 0 &&
            companies
              .filter((c) => filterCompanyIds.includes(c.id))
              .map((company) => (
                <span
                  key={company.id}
                  className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                >
                  {company.name}
                  <button
                    onClick={() =>
                      setFilterCompanyIds(
                        filterCompanyIds.filter((id) => id !== company.id)
                      )
                    }
                    className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                  >
                    ×
                  </button>
                </span>
              ))}

          {filterScheduleTypes.length > 0 &&
            filterScheduleTypes.map((type) => (
              <span
                key={type}
                className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
              >
                {getScheduleTypeLabel(type)}
                <button
                  onClick={() =>
                    setFilterScheduleTypes(
                      filterScheduleTypes.filter((t) => t !== type)
                    )
                  }
                  className="hover:bg-green-200 rounded-full p-0.5 transition-colors"
                >
                  ×
                </button>
              </span>
            ))}

          {/* すべてクリア */}
          {(filterCompanyIds.length > 0 || filterScheduleTypes.length > 0) && (
            <button
              onClick={() => {
                setFilterCompanyIds([]);
                setFilterScheduleTypes([]);
              }}
              className="text-gray-500 hover:text-gray-700 text-sm font-medium"
            >
              すべてクリア
            </button>
          )}
        </div>

        {/* フィルター詳細パネル（折りたたみ可能） */}
        {showFilter && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 企業フィルター */}
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">
                  企業
                </label>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {companies.length === 0 ? (
                    <p className="text-gray-400 text-sm p-2">
                      企業が登録されていません
                    </p>
                  ) : (
                    companies.map((company) => (
                      <label
                        key={company.id}
                        className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filterCompanyIds.includes(company.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFilterCompanyIds([
                                ...filterCompanyIds,
                                company.id,
                              ]);
                            } else {
                              setFilterCompanyIds(
                                filterCompanyIds.filter(
                                  (id) => id !== company.id
                                )
                              );
                            }
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="ml-2 text-gray-700 text-sm">
                          {company.name}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* スケジュール種類フィルター */}
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">
                  スケジュール種類
                </label>
                <div className="border border-gray-200 rounded-lg p-2">
                  {[
                    { value: "es_deadline", label: "ES締切" },
                    { value: "internship", label: "インターン" },
                    { value: "interview", label: "面接" },
                    { value: "screening", label: "グループディスカッション" },
                    { value: "orientation", label: "説明会" },
                    { value: "other", label: "その他" },
                  ].map((type) => (
                    <label
                      key={type.value}
                      className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={filterScheduleTypes.includes(type.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilterScheduleTypes([
                              ...filterScheduleTypes,
                              type.value,
                            ]);
                          } else {
                            setFilterScheduleTypes(
                              filterScheduleTypes.filter(
                                (t) => t !== type.value
                              )
                            );
                          }
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-gray-700 text-sm">
                        {type.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <ScheduleForm
          companies={companies}
          onSubmit={handleAddSchedule}
          onCancel={() => setShowForm(false)}
        />
      )}

      {editingSchedule && (
        <ScheduleForm
          companies={companies}
          onSubmit={handleEditSchedule}
          onCancel={() => setEditingSchedule(null)}
          initialData={{
            company_id: editingSchedule.company_id,
            schedule_type: editingSchedule.schedule_type,
            schedule_start_date: formatDateTimeForInput(
              editingSchedule.schedule_start_date
            ),
            schedule_end_date: formatDateTimeForInput(
              editingSchedule.schedule_end_date
            ),
          }}
          isEditing={true}
        />
      )}

      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onDelete={() => handleDeleteSchedule(selectedEvent.id)}
          onEdit={() => {
            setEditingSchedule(selectedEvent.resource);
            setSelectedEvent(null);
          }}
        />
      )}

      {/* 今日と今週の予定表示（モバイルフレンドリー） */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* 今日の予定 */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center justify-between">
            <span className="flex items-center gap-2">今日の予定</span>
            {events.filter((event) => {
              const today = new Date();
              const eventDate = new Date(event.start);
              return eventDate.toDateString() === today.toDateString();
            }).length > 3 && (
              <button
                onClick={() => setShowAllToday(!showAllToday)}
                className="text-xs text-blue-600 hover:text-blue-800 bg-transparent border-none cursor-pointer font-medium"
              >
                {showAllToday ? "折りたたむ" : "すべて表示"}
              </button>
            )}
          </h3>
          <div className="space-y-2">
            {events
              .filter((event) => {
                const today = new Date();
                const eventDate = new Date(event.start);
                return eventDate.toDateString() === today.toDateString();
              })
              .slice(0, showAllToday ? undefined : 3)
              .map((event) => (
                <div
                  key={event.id}
                  className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 cursor-pointer hover:bg-yellow-100 transition-colors"
                  onClick={() => handleSelectEvent(event)}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-yellow-800">
                      {getScheduleTypeLabel(event.resource.schedule_type)}
                    </span>
                  </div>
                  <div className="font-semibold text-gray-800 text-sm">
                    {event.resource.company_name}
                  </div>
                  <div className="text-xs text-gray-600">
                    {new Date(event.start).toLocaleTimeString("ja-JP", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              ))}
            {events.filter((event) => {
              const today = new Date();
              const eventDate = new Date(event.start);
              return eventDate.toDateString() === today.toDateString();
            }).length === 0 && (
              <div className="text-sm text-gray-400 text-center py-4">
                今日の予定はありません
              </div>
            )}
          </div>
        </div>

        {/* 今週の予定 */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center justify-between">
            <span className="flex items-center gap-2">今週の予定</span>
            {events.filter((event) => {
              const now = new Date();
              const eventDate = new Date(event.start);
              const oneWeekLater = new Date();
              oneWeekLater.setDate(now.getDate() + 7);
              return eventDate > now && eventDate <= oneWeekLater;
            }).length > 3 && (
              <button
                onClick={() => setShowAllWeek(!showAllWeek)}
                className="text-xs text-blue-600 hover:text-blue-800 bg-transparent border-none cursor-pointer font-medium"
              >
                {showAllWeek ? "折りたたむ" : "すべて表示"}
              </button>
            )}
          </h3>
          <div className="space-y-2">
            {events
              .filter((event) => {
                const now = new Date();
                const eventDate = new Date(event.start);
                const oneWeekLater = new Date();
                oneWeekLater.setDate(now.getDate() + 7);
                return eventDate > now && eventDate <= oneWeekLater;
              })
              .slice(0, showAllWeek ? undefined : 3)
              .map((event) => (
                <div
                  key={event.id}
                  className="p-3 bg-blue-50 rounded-lg border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                  onClick={() => handleSelectEvent(event)}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-blue-800">
                      {getScheduleTypeLabel(event.resource.schedule_type)}
                    </span>
                  </div>
                  <div className="font-semibold text-gray-800 text-sm">
                    {event.resource.company_name}
                  </div>
                  <div className="text-xs text-gray-600">
                    {new Date(event.start).toLocaleDateString("ja-JP", {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              ))}
            {events.filter((event) => {
              const now = new Date();
              const eventDate = new Date(event.start);
              const oneWeekLater = new Date();
              oneWeekLater.setDate(now.getDate() + 7);
              return eventDate > now && eventDate <= oneWeekLater;
            }).length === 0 && (
              <div className="text-sm text-gray-400 text-center py-4">
                今週の予定はありません
              </div>
            )}
          </div>
        </div>
      </div>

      {/* それ以降の予定（モバイル専用） */}
      <div className="md:hidden bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6">
        <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center justify-between">
          <span className="flex items-center gap-2">それ以降の予定</span>
          {events.filter((event) => {
            const now = new Date();
            const eventDate = new Date(event.start);
            const oneWeekLater = new Date();
            oneWeekLater.setDate(now.getDate() + 7);
            return eventDate > oneWeekLater;
          }).length > 5 && (
            <button
              onClick={() => setShowAllFuture(!showAllFuture)}
              className="text-xs text-blue-600 hover:text-blue-800 bg-transparent border-none cursor-pointer font-medium"
            >
              {showAllFuture ? "折りたたむ" : "すべて表示"}
            </button>
          )}
        </h3>
        <div className="space-y-2">
          {events
            .filter((event) => {
              const now = new Date();
              const eventDate = new Date(event.start);
              const oneWeekLater = new Date();
              oneWeekLater.setDate(now.getDate() + 7);
              return eventDate > oneWeekLater;
            })
            .slice(0, showAllFuture ? undefined : 5)
            .map((event) => (
              <div
                key={event.id}
                className="p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSelectEvent(event)}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-gray-800">
                    {getScheduleTypeLabel(event.resource.schedule_type)}
                  </span>
                </div>
                <div className="font-semibold text-gray-800 text-sm">
                  {event.resource.company_name}
                </div>
                <div className="text-xs text-gray-600">
                  {new Date(event.start).toLocaleDateString("ja-JP", {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            ))}
          {events.filter((event) => {
            const now = new Date();
            const eventDate = new Date(event.start);
            const oneWeekLater = new Date();
            oneWeekLater.setDate(now.getDate() + 7);
            return eventDate > oneWeekLater;
          }).length === 0 && (
            <div className="text-sm text-gray-400 text-center py-4">
              それ以降の予定はありません
            </div>
          )}
        </div>
      </div>

      {/* カレンダー（PC専用） */}
      <div className="hidden md:block bg-white rounded-xl p-6 shadow-sm relative">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 700 }}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventStyleGetter}
          views={["month", "week", "day"]}
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          defaultView="month"
          messages={{
            next: "来月",
            previous: "前月",
            today: "今日",
            month: "月",
            week: "週",
            day: "日",
            date: "日付",
            time: "時間",
            event: "イベント",
            noEventsInRange: "この期間にイベントはありません",
          }}
        />
        {schedules.length === 0 && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center py-12 px-8 bg-white/95 rounded-xl border-2 border-dashed border-gray-300 z-10 pointer-events-none">
            <h3 className="text-gray-600 mb-2">
              スケジュールが登録されていません
            </h3>
            <p className="text-gray-400">
              「スケジュールを追加」ボタンからスケジュール情報を登録してください。
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ヘルパー関数
function getScheduleTypeLabel(type: string): string {
  const typeMap: { [key: string]: string } = {
    es_deadline: "ES締切",
    internship: "インターン",
    interview: "面接",
    screening: "グループディスカッション",
    orientation: "説明会",
    other: "その他",
  };
  return typeMap[type] || type;
}

function formatDateTimeForInput(dateString: string): string {
  // データベースから取得した日時文字列をdatetime-local input用の形式に変換
  // ISO形式 (YYYY-MM-DDTHH:mm:ss.sssZ) → YYYY-MM-DDTHH:mm
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function getScheduleTypeClass(type: string): string {
  const classMap: { [key: string]: string } = {
    es_deadline: "type-deadline",
    internship: "type-internship",
    interview: "type-interview",
    screening: "type-screening",
    orientation: "type-orientation",
    other: "type-other",
  };
  return classMap[type] || "type-other";
}

// イベント詳細モーダル
interface EventDetailModalProps {
  event: CalendarEvent;
  onClose: () => void;
  onDelete: () => void;
  onEdit: () => void;
}

const EventDetailModal: React.FC<EventDetailModalProps> = ({
  event,
  onClose,
  onDelete,
  onEdit,
}) => {
  const schedule = event.resource;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("ja-JP", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getBadgeColor = (type: string) => {
    const colorMap: { [key: string]: string } = {
      "type-deadline": "bg-gradient-to-br from-red-600 to-red-800",
      "type-internship": "bg-gradient-to-br from-blue-500 to-blue-700",
      "type-interview": "bg-gradient-to-br from-yellow-500 to-orange-500",
      "type-screening": "bg-gradient-to-br from-purple-600 to-purple-800",
      "type-orientation": "bg-gradient-to-br from-teal-500 to-teal-700",
      "type-other": "bg-gradient-to-br from-gray-500 to-gray-600",
    };
    return colorMap[type] || colorMap["type-other"];
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1000] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl w-full max-w-lg shadow-2xl animate-[modalSlideIn_0.3s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="m-0 text-gray-800 text-2xl">スケジュール詳細</h2>
          <button
            className="bg-transparent border-none text-2xl text-gray-400 cursor-pointer py-1 px-2 rounded transition-colors hover:bg-gray-100 hover:text-gray-800"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4 flex items-center">
            <span
              className={`inline-block py-2 px-4 rounded-md text-sm font-semibold text-white mb-2 ${getBadgeColor(
                getScheduleTypeClass(schedule.schedule_type)
              )}`}
            >
              {getScheduleTypeLabel(schedule.schedule_type)}
            </span>
          </div>

          <div className="mb-4 flex items-center flex-col md:flex-row md:items-start">
            <span className="font-semibold text-gray-600 min-w-[80px] mr-4 mb-1 md:mb-0">
              企業名:
            </span>
            <span className="text-gray-800 flex-1">
              {schedule.company_name}
            </span>
          </div>

          <div className="mb-4 flex items-center flex-col md:flex-row md:items-start">
            <span className="font-semibold text-gray-600 min-w-[80px] mr-4 mb-1 md:mb-0">
              開始日時:
            </span>
            <span className="text-gray-800 flex-1">
              {formatDate(schedule.schedule_start_date)}
            </span>
          </div>

          <div className="mb-4 flex items-center flex-col md:flex-row md:items-start">
            <span className="font-semibold text-gray-600 min-w-[80px] mr-4 mb-1 md:mb-0">
              終了日時:
            </span>
            <span className="text-gray-800 flex-1">
              {formatDate(schedule.schedule_end_date)}
            </span>
          </div>

          <div className="flex items-center flex-col md:flex-row md:items-start">
            <span className="font-semibold text-gray-600 min-w-[80px] mr-4 mb-1 md:mb-0">
              登録日:
            </span>
            <span className="text-gray-800 flex-1">
              {formatDate(schedule.created_at)}
            </span>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex gap-4 justify-end flex-col md:flex-row">
          <button
            className="bg-red-600 text-white border-none py-3 px-6 rounded-lg text-base cursor-pointer transition-all hover:bg-red-700 hover:-translate-y-0.5"
            onClick={onDelete}
          >
            削除
          </button>
          <button
            className="bg-blue-600 text-white border-none py-3 px-6 rounded-lg text-base cursor-pointer transition-all hover:bg-blue-700 hover:-translate-y-0.5"
            onClick={onEdit}
          >
            編集
          </button>
          <button
            className="bg-gray-100 text-gray-600 border border-gray-300 py-3 px-6 rounded-lg text-base cursor-pointer transition-all hover:bg-gray-200"
            onClick={onClose}
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};

// スケジュール登録フォーム
interface ScheduleFormProps {
  companies: Company[];
  onSubmit: (data: CreateScheduleRequest) => void;
  onCancel: () => void;
  initialData?: CreateScheduleRequest;
  isEditing?: boolean;
}

const ScheduleForm: React.FC<ScheduleFormProps> = ({
  companies,
  onSubmit,
  onCancel,
  initialData,
  isEditing = false,
}) => {
  const [formData, setFormData] = useState<CreateScheduleRequest>(
    initialData || {
      company_id: 0,
      schedule_type: "",
      schedule_start_date: "",
      schedule_end_date: "",
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !formData.company_id ||
      !formData.schedule_type ||
      !formData.schedule_start_date ||
      !formData.schedule_end_date
    ) {
      alert("すべての項目を入力してください");
      return;
    }
    onSubmit(formData);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "company_id" ? Number(value) : value,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4">
      <form
        className="bg-white rounded-xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl animate-[modalSlideIn_0.3s_ease-out]"
        onSubmit={handleSubmit}
      >
        <h2 className="m-0 mb-6 text-gray-800 text-center text-2xl">
          {isEditing ? "スケジュール編集" : "スケジュール登録"}
        </h2>

        <div className="mb-6">
          <label
            htmlFor="company_id"
            className="block mb-2 font-medium text-gray-800"
          >
            企業 *
          </label>
          <select
            id="company_id"
            name="company_id"
            value={formData.company_id}
            onChange={handleChange}
            required
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          >
            <option value={0}>企業を選択してください</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-6">
          <label
            htmlFor="schedule_type"
            className="block mb-2 font-medium text-gray-800"
          >
            スケジュール種類 *
          </label>
          <select
            id="schedule_type"
            name="schedule_type"
            value={formData.schedule_type}
            onChange={handleChange}
            required
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          >
            <option value="">種類を選択してください</option>
            <option value="es_deadline">ES締切</option>
            <option value="internship">インターン</option>
            <option value="interview">面接</option>
            <option value="screening">グループディスカッション</option>
            <option value="orientation">説明会</option>
            <option value="other">その他</option>
          </select>
        </div>

        <div className="mb-6">
          <label
            htmlFor="schedule_start_date"
            className="block mb-2 font-medium text-gray-800"
          >
            開始日時 *
          </label>
          <input
            type="datetime-local"
            id="schedule_start_date"
            name="schedule_start_date"
            value={formData.schedule_start_date}
            onChange={handleChange}
            required
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          />
        </div>

        <div className="mb-6">
          <label
            htmlFor="schedule_end_date"
            className="block mb-2 font-medium text-gray-800"
          >
            終了日時 *
          </label>
          <input
            type="datetime-local"
            id="schedule_end_date"
            name="schedule_end_date"
            value={formData.schedule_end_date}
            onChange={handleChange}
            required
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          />
        </div>

        <div className="flex gap-4 justify-end mt-8 flex-col md:flex-row">
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-100 text-gray-600 border border-gray-300 py-3 px-6 rounded-lg text-base cursor-pointer transition-all hover:bg-gray-200"
          >
            キャンセル
          </button>
          <button
            type="submit"
            className="bg-gradient-to-br from-blue-500 to-green-600 text-white border-none py-3 px-6 rounded-lg text-base font-medium cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-lg"
          >
            {isEditing ? "更新" : "登録"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SchedulesPage;
